import { useCallback, useEffect, useState } from 'react';
import api from '../api';
import { QUALITY_SERVICE_API_URL } from '../config';

async function fetchProductionIncidentByOrigin(breakdown, queryString) {
  const url = `${QUALITY_SERVICE_API_URL}/hub/production-incidents/by-origin?breakdown=${breakdown}&${queryString}`;
  try {
    const response = await api.get(url);
    return response.data;
  } catch (err) {
    console.error(`[useProductionIncidentByOrigin] ${breakdown} failed:`, {
      status: err.response?.status,
      message: err.message,
    });
    throw err;
  }
}

function useProductionIncidentByOrigin(breakdown, filters, dateRange) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Build query string
  const params = new URLSearchParams();
  ['domain', 'group', 'unit'].forEach(key => {
    (filters?.[key] || []).forEach(value => params.append(key, value));
  });

  const formatDate = date => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  if (dateRange?.startDate) params.set('from', formatDate(dateRange.startDate));
  if (dateRange?.endDate) params.set('to', formatDate(dateRange.endDate));

  const queryString = params.toString();

  const fetchByOrigin = useCallback(async (isCancelled = () => false) => {
    if (!breakdown || !dateRange?.startDate || !dateRange?.endDate) {
      setData(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    setData(null);
    try {
      const by_origin_data = await fetchProductionIncidentByOrigin(breakdown, queryString);
      if (!isCancelled()) setData(by_origin_data);
    } catch (err) {
      if (!isCancelled()) setError(err);
    } finally {
      if (!isCancelled()) setLoading(false);
    }
  }, [breakdown, queryString, dateRange?.startDate, dateRange?.endDate]);

  useEffect(() => {
    let cancelled = false;
    fetchByOrigin(() => cancelled);
    return () => { cancelled = true; };
  }, [fetchByOrigin]);

  return { data, loading, error, refetch: () => fetchByOrigin() };
}

export default useProductionIncidentByOrigin;
